import { Model } from '@vuex-orm/core';
import api, { downloadFile } from '@/api';
import joinPath from 'path.join';
import pick from 'lodash.pick';

function throwRequiredAttributeError(attr) {
  throw new Error(`${attr} needs to be set when inheriting from BaseModel: static ${attr} = 'value';`);
}

export default class BaseModel extends Model {
  static get apiPath() { return throwRequiredAttributeError('apiPath'); }
  static get routeName() { return throwRequiredAttributeError('routeName'); }

  static async fetch(id) {
    const { data } = await api.get(joinPath(this.apiPath, id.toString()));
    await this.dispatch('insertOrUpdate', { data });
    return this.find(id);
  }

  static async fetchAll() {
    const { data } = await api.get(this.apiPath);
    await this.dispatch('insertOrUpdate', { data });
    return this.all();
  }

  static async fetchRelated(entity, id) {
    const tempInstance = new this({ $id: id });
    const related = this.relation(entity);
    const { data } = await api.get(joinPath(tempInstance.apiPath, related.apiPath));
    const inserted = await related.dispatch('insertOrUpdate', { data });
    return inserted[entity];
  }


  static async fetchAllByRelations(...relations) {
    const path = relations.reduce((a, b) => joinPath(b.apiPath, a.apiPath), this);
    const { data } = await api.get(path);
    return this.dispatch('insertOrUpdate', { data });
  }

  static async findOrFetch(id) {
    const record = this.find(id);
    return record ? Promise.resolve(record) : this.fetch(id);
  }

  static date(value) {
    /*
       A date field is either null (empty string is casted to null)
       or a string containing the date.
       Empty string will fail in the backend, because it tries to match
       the format (like ISO) of empty string.
    */
    return this.attr(value, v => (v || null));
  }

  async save() {
    const createdRecord = await api.post(this.constructor.apiPath, this.$toJson());
    const stored = await this.$insert(createdRecord);
    return stored[this.constructor.entity][0];
  }

  async destroy() {
    await api.delete(this.apiPath);
    return this.$delete();
  }

  async update(keys = []) {
    if (keys.length) {
      const toUpdate = this.pickKeys(keys);
      const { data } = await api.patch(this.apiPath, toUpdate);
      return this.$update(data);
    }
    const { data } = await api.patch(this.apiPath, this.$toJson());
    return this.$update(data);
  }

  get apiPath() {
    return joinPath(this.constructor.apiPath, this.$id.toString());
  }

  get url() {
    return {
      name: this.constructor.routeName,
      params: {
        id: this.id,
      },
    };
  }

  get editURL() {
    return {
      name: `edit-${this.constructor.routeName}`,
      params: {
        id: this.id,
      },
    };
  }

  get listKey() {
    return `${this.constructor.entity}-${this.id}`;
  }

  pickKeys(keys) {
    const data = this.$toJson();
    const defaultKeys = Object.keys(data);
    return pick(data, keys || defaultKeys);
  }

  async downloadZip(filenameAttribute = 'title') {
    const { data } = await api.get(
      joinPath(this.apiPath, 'download-zip'),
      { responseType: 'blob' },
    );
    downloadFile(data, `${this[filenameAttribute]}.zip`);
  }

  get publishingDatetime() {
    if (this.publishing_datetime) {
      return this.publishing_datetime.substr(0, 19);
    }

    return null;
  }

  set publishingDatetime(val) {
    this.publishing_datetime = val;
  }
}
